/**
 * Düzenlenebilir plan tablosu.
 *
 * Sıra değiştirme iki yoldan: tutamaktan sürükle-bırak ya da satırdaki
 * yukarı/aşağı düğmeleri. Sürükleme kalıbı AsmaSPV ekranındakiyle aynı
 * (antd Table + @dnd-kit/sortable); tek fark satır bileşeninin modül
 * seviyesinde tanımlı olması — bileşen içinde tanımlanınca her render'da
 * satırlar baştan kuruluyor.
 *
 * Zaman sütunu satırla birlikte TAŞINMAZ: plan konumsal, N. sıra N. periyoda
 * denk gelir (bkz. plan.js).
 *
 * Asma kilidi: baştaki `kilitSayisi` satır asmaya girmiş; taşınamaz,
 * düzenlenemez, silinemez, üstlerine başka satır da taşınamaz. Şu an
 * asmadaki satır ayrıca işaretlenir. Kilidin hesabı plan.js'te (hatDurumu).
 */
import React from "react";
import { Button, Popconfirm, Table, Tag, Tooltip } from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  EditOutlined,
  HolderOutlined,
  LockOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTranslation } from "react-i18next";
import { sayiYaz } from "./bicim";
import "./Simulation.css";

/** Renk etiketinin tonu — rapordaki renk adlarıyla. */
const RENK_TONU = { Beyaz: "default", Silver: "blue", "Cast Iron": "volcano" };

/**
 * Sürüklenebilir satır. Dinleyiciler yalnız tutamak hücresine bağlanır;
 * satırdaki düğmelere tıklamak sürükleme başlatmaz.
 *
 * Kilitli satır (`data-kilitli="1"`, tablonun onRow'undan gelir) ne
 * sürüklenebilir ne de üstüne bırakılabilir — tutamak yerine kilit gösterilir.
 */
function SiralanabilirSatir({ children, ...props }) {
  const { t } = useTranslation();
  const kilitli = props["data-kilitli"] === "1";
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props["data-row-key"], disabled: kilitli });

  const style = {
    ...props.style,
    // Yalnız dikey eksende kayar.
    transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 })?.replace(
      /translate3d\(([^,]+),/,
      "translate3d(0,"
    ),
    transition,
    ...(isDragging ? { position: "relative", zIndex: 999 } : {}),
  };

  const tutamak = kilitli ? (
    <Tooltip title={t("simulation.lock.lockedTip")}>
      <LockOutlined />
    </Tooltip>
  ) : (
    <HolderOutlined
      ref={setActivatorNodeRef}
      style={{ touchAction: "none", cursor: "grab" }}
      {...listeners}
    />
  );

  return (
    <tr
      {...props}
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`${props.className ?? ""}${isDragging ? " sim-surukle" : ""}`}
    >
      {React.Children.map(children, (child) =>
        child?.key === "tutamak" ? React.cloneElement(child, { children: tutamak }) : child
      )}
    </tr>
  );
}

const BILESENLER = { body: { row: SiralanabilirSatir } };

function MalzemeHucresi({ kod, sozluk }) {
  const { t } = useTranslation();
  if (!kod) return <span className="text-content-faint">—</span>;
  const m = sozluk.get(Number(kod));
  return (
    <div className="leading-tight">
      <div className="text-[13px] text-content">{m?.ad ?? t("simulation.notInList")}</div>
      <div className="text-[11px] text-content-faint">{kod}</div>
    </div>
  );
}

export default function PlanTablosu({
  satirlar,
  zamanlar,
  govdeSozluk,
  govdeOnSozluk,
  renkSiralari,
  eksikler,
  kilitSayisi = 0,
  asmadaKonum = -1,
  mesgul,
  onSirala,
  onTasi,
  onDuzenle,
  onArayaEkle,
  onDurusDegis,
  onSil,
}) {
  const { t } = useTranslation();
  const col = (k) => t(`simulation.cols.${k}`);
  const eylem = (k) => t(`simulation.actions.${k}`);
  const kilitliMi = (i) => i < kilitSayisi;

  // Küçük bir kayma eşiği: tutamağa tıklamak kazara sürükleme başlatmasın.
  const sensorler = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  const suruklemeBitti = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const kaynak = satirlar.findIndex((s) => s.key === active.id);
    const hedef = satirlar.findIndex((s) => s.key === over.id);
    // Kilitli bölgeye taşıma yok (üst bileşen de ayrıca denetliyor).
    if (kaynak < kilitSayisi || hedef < kilitSayisi) return;
    if (kaynak >= 0 && hedef >= 0) onSirala(kaynak, hedef);
  };

  const sutunlar = [
    { key: "tutamak", width: 34, align: "center" },
    {
      title: col("seq"),
      key: "sira",
      width: 56,
      render: (_, __, i) => <b className="text-content">{i + 1}</b>,
    },
    {
      title: col("time"),
      key: "zaman",
      width: 116,
      render: (_, __, i) => {
        const z = zamanlar[i];
        // Taban plandan uzun bir plan: eklenen satırın periyodu yok.
        if (!z) return <span className="text-content-faint">—</span>;
        return (
          <div className="leading-tight">
            <div className="text-[13px] text-content">{z.periyot}</div>
            {i === asmadaKonum ? (
              <Tag color="gold" className="m-0 mt-0.5 px-1 text-[10px] leading-4">
                ▶ {t("simulation.lock.current")}
              </Tag>
            ) : (
              <div className="text-[11px] text-content-faint">{z.tarih}</div>
            )}
          </div>
        );
      },
    },
    {
      title: col("color"),
      key: "renk",
      width: 96,
      render: (_, s) => {
        // Renk adları (Beyaz/Silver/Cast Iron) veri değeri; çevrilmez.
        if (s.durus) return <Tag>{t("simulation.stop")}</Tag>;
        const renk = govdeSozluk.get(Number(s.govde))?.renk;
        return renk ? <Tag color={RENK_TONU[renk]}>{renk}</Tag> : "—";
      },
    },
    {
      title: col("body"),
      key: "govde",
      render: (_, s) => <MalzemeHucresi kod={s.govde} sozluk={govdeSozluk} />,
    },
    {
      title: col("frontBody"),
      key: "govde_on",
      render: (_, s) => <MalzemeHucresi kod={s.govde_on} sozluk={govdeOnSozluk} />,
    },
    {
      title: col("qty"),
      dataIndex: "uretim",
      width: 72,
      align: "right",
      render: (v, s) => (s.durus ? "—" : sayiYaz(v)),
    },
    {
      title: "",
      key: "islem",
      width: 196,
      // Kilitli satırda eylem yok; kilit simgesi tutamak hücresinde.
      render: (_, s, i) =>
        kilitliMi(i) ? null : (
          <div className="flex items-center gap-0.5">
            <Tooltip title={eylem("moveUp")}>
              <Button
                size="small"
                type="text"
                icon={<ArrowUpOutlined />}
                // İlk açık satır yukarı çıkarsa kilitli bölgeye girer.
                disabled={mesgul || i <= kilitSayisi}
                onClick={() => onTasi(i, -1)}
              />
            </Tooltip>
            <Tooltip title={eylem("moveDown")}>
              <Button
                size="small"
                type="text"
                icon={<ArrowDownOutlined />}
                disabled={mesgul || i === satirlar.length - 1}
                onClick={() => onTasi(i, 1)}
              />
            </Tooltip>
            <Tooltip title={eylem("edit")}>
              <Button
                size="small"
                type="text"
                icon={<EditOutlined />}
                disabled={mesgul}
                onClick={() => onDuzenle(i)}
              />
            </Tooltip>
            <Tooltip title={s.durus ? eylem("unstop") : eylem("makeStop")}>
              <Button
                size="small"
                type="text"
                icon={s.durus ? <PlayCircleOutlined /> : <PauseCircleOutlined />}
                disabled={mesgul}
                onClick={() => onDurusDegis(i)}
              />
            </Tooltip>
            <Tooltip title={eylem("insertBelow")}>
              <Button
                size="small"
                type="text"
                icon={<PlusOutlined />}
                disabled={mesgul}
                onClick={() => onArayaEkle(i)}
              />
            </Tooltip>
            <Popconfirm
              title={eylem("deleteRowConfirm")}
              okText={t("simulation.delete")}
              cancelText={t("simulation.cancel")}
              onConfirm={() => onSil(i)}
              disabled={mesgul}
            >
              <Tooltip title={t("simulation.delete")}>
                <Button
                  size="small"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={mesgul}
                />
              </Tooltip>
            </Popconfirm>
          </div>
        ),
    },
  ];

  const satirSinifi = (s, i) =>
    [
      i === asmadaKonum ? "sim-asmada-satir" : kilitliMi(i) && "sim-kilitli-satir",
      !kilitliMi(i) && s.durus && "sim-durus-satir",
      !kilitliMi(i) && eksikler.has(i + 1) && "sim-eksik-satir",
      renkSiralari.has(i + 1) && "sim-renk-satir",
    ]
      .filter(Boolean)
      .join(" ");

  return (
    <DndContext sensors={sensorler} onDragEnd={suruklemeBitti}>
      <SortableContext
        items={satirlar.map((s) => s.key)}
        strategy={verticalListSortingStrategy}
        disabled={mesgul}
      >
        <Table
          className="sim-tablo"
          size="small"
          sticky
          rowKey="key"
          components={BILESENLER}
          dataSource={satirlar}
          columns={sutunlar}
          rowClassName={satirSinifi}
          // Satır bileşeni kilidi buradan okur.
          onRow={(_, i) => ({ "data-kilitli": kilitliMi(i) ? "1" : "0" })}
          pagination={false}
          scroll={{ x: 900 }}
          locale={{ emptyText: t("simulation.warn.emptyPlan") }}
        />
      </SortableContext>
    </DndContext>
  );
}
